import React, { SVGProps } from "react";
import { User, Eye, Heart } from "lucide-react";
import Link from "next/link";

interface CardContentProps {
  name: string;
  title?: string;
  location?: string;
  imageSrc?: string;
  isMobile?: boolean;
  viewCount: number;
  likeCount: number;
  isLiked: boolean;
  skills?: string[];
  onLikeToggle?: (e: React.MouseEvent) => void;
  profilePath?: string;
  hire_me_enabled?: boolean;
  is_featured?: boolean;
}

export const VerifiedBadge = (props: SVGProps<SVGSVGElement>) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      fill="none"
      viewBox="0 0 22 22"
      {...props}
    >
      <g clipPath="url(#a)">
        <mask
          id="b"
          width="100%"
          height="100%"
          x={0}
          y={0}
          maskUnits="userSpaceOnUse"
          style={{
            maskType: "luminance",
          }}
        >
          <path fill="#fff" d="M22 0H0v22h22V0Z" />
        </mask>
        <g mask="url(#b)">
          <path
            fill="#271619"
            d="M12.419 1.202a1.833 1.833 0 0 0-2.837 0l-.91 1.11-1.342-.506a1.833 1.833 0 0 0-2.457 1.418L4.641 4.64l-1.416.233A1.833 1.833 0 0 0 1.806 7.33l.507 1.342-1.11.91a1.833 1.833 0 0 0 0 2.836l1.11.91-.507 1.342a1.833 1.833 0 0 0 1.419 2.457l1.416.233.232 1.416a1.833 1.833 0 0 0 2.457 1.418l1.343-.507.909 1.11a1.833 1.833 0 0 0 2.837 0l.91-1.11 1.342.507a1.833 1.833 0 0 0 2.457-1.418l.232-1.416 1.416-.233a1.833 1.833 0 0 0 1.419-2.457l-.507-1.342 1.11-.91a1.833 1.833 0 0 0 0-2.837l-1.11-.909.507-1.342a1.833 1.833 0 0 0-1.419-2.457L17.36 4.64l-.232-1.416a1.833 1.833 0 0 0-2.457-1.418l-1.343.507-.909-1.11Z"
          />
          <path
            fill="#FC9339"
            fillRule="evenodd"
            d="m16.69 8.44-6.417 6.417a.917.917 0 0 1-1.296 0L5.31 11.19l1.297-1.297 3.018 3.019 5.769-5.769L16.69 8.44Z"
            clipRule="evenodd"
          />
        </g>
      </g>
      <defs>
        <clipPath id="a">
          <path fill="#fff" d="M0 0h22v22H0z" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const CardContent = ({
  name,
  title,
  location,
  imageSrc,
  isMobile = false,
  viewCount,
  likeCount,
  isLiked,
  skills = [],
  onLikeToggle,
  profilePath = "#",
  hire_me_enabled = false,
  is_featured = false,
}: CardContentProps) => {
  if (isMobile) {
    return (
      <div className="space-y-2 p-3">
        <div className="flex items-center justify-between">
          <div
            className="flex gap-1 items-center max-w-[60%]" // limit width for name+badge
            title={
              is_featured
                ? "This badge means this designer is verified by our team"
                : undefined
            }
          >
            <p className="text-sm font-medium truncate max-w-[120px] md:max-w-[180px]">
              {name}
            </p>
            {is_featured && <VerifiedBadge width={14} height={14} />}
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center">
              <Eye className="h-3.5 w-3.5 mr-1 shrink-0 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                {viewCount.toLocaleString()}
              </p>
            </div>
            <div
              className={`flex items-center cursor-pointer transition-opacity opacity-100`}
              onClick={onLikeToggle}
            >
              <Heart
                className={`h-3.5 w-3.5 mr-1 shrink-0 text-muted-foreground ${
                  isLiked ? "fill-current text-red-500" : ""
                }`}
              />
              <p className="text-xs text-muted-foreground">{likeCount}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="flex justify-between items-center w-full">
        {/* Designer name at the left */}
        <div
          className="flex gap-1 items-center max-w-[60%]" // limit width for name+badge
          title={
            is_featured
              ? "This badge means this designer is verified by our team"
              : undefined
          }
        >
          <p className="text-sm font-medium truncate max-w-[120px] md:max-w-[180px]">
            {name}
          </p>
          {is_featured && <VerifiedBadge width={14} height={14} />}
        </div>

        {/* Stats at the right */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center text-xs text-muted-foreground">
            <Eye className="h-3.5 w-3.5 mr-1 shrink-0" />
            <p>{viewCount.toLocaleString()}</p>
          </div>
          <div
            className={`flex items-center text-xs text-muted-foreground cursor-pointer transition-opacity opacity-100`}
            onClick={onLikeToggle}
          >
            <Heart
              className={`h-3.5 w-3.5 mr-1 shrink-0 ${
                isLiked ? "fill-current text-red-500" : ""
              }`}
            />
            <p>{likeCount}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
